import * as React from "react";
import { Button, Form } from "react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import { gettext } from "i18n";
import {
  METHOD_DESCRIPTIONS,
  METHOD_EMAIL,
  METHOD_LABELS,
  METHOD_PASSKEY,
  METHOD_TOTP,
  MfaMethodKind,
  addPasskey,
  beginEmailMethod,
  beginTotp,
  confirmEmailMethod,
  confirmTotp,
  passkeysSupported,
} from "utils/mfa";
import { StepUpCancelled, useStepUp } from "./StepUpProvider";

interface SetupProps {
  // Called once the method is in use, with the backup codes if it turned MFA on
  onAdded: (backupCodes: string[] | null) => void;
  onCancel?: () => void;
}

const errorMessage = (e: unknown): string | null => {
  // The user closed the second factor dialog: nothing to say
  if (e instanceof StepUpCancelled) {
    return null;
  }
  // A passkey prompt the user closed
  if (e instanceof DOMException && e.name === "NotAllowedError") {
    return gettext("The passkey was not created. Please try again.");
  }
  return gettext((e as Error)?.message ?? String(e));
};

/** Adds an authenticator app: scan the QR code, then enter a code from the app */
export const TotpSetup: React.FC<SetupProps> = ({ onAdded, onCancel }) => {
  // Adding a method to an account with MFA needs a recent second factor, which
  // may have run out while the user was setting up the app
  const withStepUp = useStepUp();
  const [setup, setSetup] = React.useState<{
    method_id: number;
    secret: string;
    otpauth_uri: string;
  } | null>(null);
  const [code, setCode] = React.useState<string>("");
  const [label, setLabel] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [showSecret, setShowSecret] = React.useState<boolean>(false);

  // The latest, for the setup started once
  const onCancelRef = React.useRef(onCancel);
  onCancelRef.current = onCancel;

  React.useEffect(() => {
    // Starting needs the recent second factor too, e.g. on the forced setup
    // page more than a few minutes after logging in
    withStepUp(() => beginTotp())
      .then(setSetup)
      .catch((e) => {
        if (e instanceof StepUpCancelled && onCancelRef.current) {
          onCancelRef.current();
          return;
        }
        setError(errorMessage(e) ?? gettext("Cancelled"));
      });
  }, [withStepUp]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setup || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await withStepUp(() =>
        confirmTotp(
          setup.method_id,
          code,
          label || gettext("Authenticator app"),
        ),
      );
      onAdded(result.backup_codes ?? null);
    } catch (e) {
      setError(errorMessage(e));
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  if (!setup) {
    return error ? (
      <p className="text-danger">{error}</p>
    ) : (
      <p>{gettext("Loading...")}</p>
    );
  }

  return (
    <Form onSubmit={onSubmit} className="TotpSetup">
      {/* Every step, including the optional name, is a list item so that the
          inputs line up with the step texts */}
      <ol className="ps-3">
        <li className="mb-3">
          {gettext(
            "Install an authenticator app on your phone, e.g. Google Authenticator, Microsoft Authenticator or Authy.",
          )}
        </li>
        <li className="mb-3">
          {gettext("Scan this QR code with the app:")}
          {/* The white padding is the quiet zone scanners need around the code */}
          <div className="mt-2 mb-1">
            <div className="d-inline-block p-3 bg-white border rounded">
              <QRCodeSVG value={setup.otpauth_uri} size={160} />
            </div>
          </div>
          <Button
            variant="link"
            className="p-0 text-start"
            onClick={() => setShowSecret(!showSecret)}
          >
            {gettext("Can't scan the code? Enter this key in the app instead")}
          </Button>
          {showSecret && (
            <div className="mt-1 font-monospace user-select-all text-break">
              {setup.secret.match(/.{1,4}/g)?.join(" ")}
            </div>
          )}
        </li>
        <li className="mb-3">
          {gettext("Enter the 6-digit code the app shows:")}
          <Form.Control
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={10}
            className="mt-2"
            aria-label={gettext("Code")}
          />
        </li>
        <li className="mb-3">
          <label htmlFor="totp-setup-label" className="fs-0 fw-normal mb-0">
            {gettext("Name (optional)")}
          </label>
          <Form.Control
            id="totp-setup-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={gettext("e.g. My phone")}
            maxLength={64}
            className="mt-2"
          />
        </li>
      </ol>
      {error && <p className="text-danger">{error}</p>}
      <div className="d-flex flex-column-reverse flex-sm-row gap-2">
        {onCancel && (
          <Button variant="light" onClick={onCancel}>
            {gettext("Cancel")}
          </Button>
        )}
        <Button type="submit" disabled={submitting || !code.trim()}>
          {gettext("Verify and turn on")}
        </Button>
      </div>
    </Form>
  );
};

/** Adds a passkey: the browser asks for a fingerprint, face, PIN or security key */
export const PasskeySetup: React.FC<SetupProps> = ({ onAdded, onCancel }) => {
  const withStepUp = useStepUp();
  const [label, setLabel] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const create = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await withStepUp(() =>
        addPasskey(label || gettext("Passkey")),
      );
      onAdded(result.backup_codes ?? null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="PasskeySetup">
      <p>
        {gettext(
          "Your browser will ask you to create a passkey with your fingerprint, face, screen lock or security key.",
        )}
      </p>
      <Form.Group className="mb-3">
        <Form.Label>{gettext("Name (optional)")}</Form.Label>
        <Form.Control
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={gettext("e.g. Work laptop or YubiKey")}
          maxLength={64}
        />
      </Form.Group>
      {error && <p className="text-danger">{error}</p>}
      <div className="d-flex flex-column-reverse flex-sm-row gap-2">
        {onCancel && (
          <Button variant="light" onClick={onCancel}>
            {gettext("Cancel")}
          </Button>
        )}
        <Button disabled={submitting} onClick={create}>
          {gettext("Create passkey")}
        </Button>
      </div>
    </div>
  );
};

/** Turns on email codes: the user enters a code sent to them */
export const EmailSetup: React.FC<SetupProps> = ({ onAdded, onCancel }) => {
  const withStepUp = useStepUp();
  const [emailHint, setEmailHint] = React.useState<string | null>(null);
  const [code, setCode] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const send = async () => {
    setError(null);
    try {
      const result = await withStepUp(() => beginEmailMethod());
      setEmailHint(result.email_hint);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await withStepUp(() => confirmEmailMethod(code));
      onAdded(result.backup_codes ?? null);
    } catch (e) {
      setError(errorMessage(e));
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={onSubmit} className="EmailSetup">
      {emailHint === null ? (
        <p>
          {gettext(
            "We will send a code to your email address to check that you receive the codes.",
          )}
        </p>
      ) : (
        <>
          <p>{gettext("Enter the code sent to %1.", emailHint)}</p>
          <Form.Control
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={10}
            className="mb-3"
            aria-label={gettext("Code")}
          />
        </>
      )}
      {error && <p className="text-danger">{error}</p>}
      <div className="d-flex flex-column-reverse flex-sm-row gap-2">
        {onCancel && (
          <Button variant="light" onClick={onCancel}>
            {gettext("Cancel")}
          </Button>
        )}
        {emailHint === null ? (
          <Button onClick={send}>{gettext("Send code")}</Button>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={send}>
              {gettext("Send a new code")}
            </Button>
            <Button type="submit" disabled={submitting || !code.trim()}>
              {gettext("Verify and turn on")}
            </Button>
          </>
        )}
      </div>
    </Form>
  );
};

interface MfaMethodSetupProps extends SetupProps {
  allowedMethods: MfaMethodKind[];
  // Methods the user already has that cannot be added twice (e.g. email)
  existingMethods?: MfaMethodKind[];
}

/** Lets the user pick a method to add, then sets it up */
const MfaMethodSetup: React.FC<MfaMethodSetupProps> = ({
  allowedMethods,
  existingMethods = [],
  onAdded,
  onCancel,
}) => {
  const choices = allowedMethods.filter(
    (method) =>
      (method !== METHOD_PASSKEY || passkeysSupported()) &&
      (method !== METHOD_EMAIL || !existingMethods.includes(METHOD_EMAIL)),
  );
  const [method, setMethod] = React.useState<MfaMethodKind | null>(
    choices.length === 1 ? choices[0] : null,
  );

  if (method === METHOD_TOTP) {
    return <TotpSetup onAdded={onAdded} onCancel={() => setMethod(null)} />;
  }
  if (method === METHOD_PASSKEY) {
    return <PasskeySetup onAdded={onAdded} onCancel={() => setMethod(null)} />;
  }
  if (method === METHOD_EMAIL) {
    return <EmailSetup onAdded={onAdded} onCancel={() => setMethod(null)} />;
  }

  return (
    <div className="MfaMethodSetup">
      <p>{gettext("Choose how you want to confirm your logins:")}</p>
      <div className="d-grid gap-2 mb-3">
        {choices.map((choice) => (
          <Button
            key={`setup_${choice}`}
            variant="outline-primary"
            className="text-start py-2"
            onClick={() => setMethod(choice)}
          >
            <strong>{gettext(METHOD_LABELS[choice])}</strong>
            {choice === METHOD_TOTP &&
              existingMethods.includes(METHOD_TOTP) && (
                <> ({gettext("replaces your current app")})</>
              )}
            <small className="d-block mt-1 lh-sm">
              {gettext(METHOD_DESCRIPTIONS[choice])}
            </small>
          </Button>
        ))}
      </div>
      {onCancel && (
        <Button variant="light" onClick={onCancel}>
          {gettext("Cancel")}
        </Button>
      )}
    </div>
  );
};

export default MfaMethodSetup;
